import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { Card, FocusEditor, StatusBadge } from '@shared/design-system/ui'
import type { FocusSaveState } from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { parseProductResponse, productKeys } from '@entities/product/api/product.queries'
import { useUpdateCheckoutMutation } from '@entities/product/api/product.mutations'
import type { Product, ProductCheckout } from '@entities/product/api/product.contract'
import { apiFetch } from '@shared/api/http'
import { applyCheckoutSkinPatch, checkoutForSkin, type CheckoutSkin } from '../lib/checkoutStyle'
import { CheckoutConfigControls, thankYouUrlError } from './CheckoutConfigControls'
import { isHttpsRedirectUrl } from '@shared/lib/url/https-redirect-url'
import { CheckoutPreview } from './CheckoutPreview'
import type { PreviewDevice } from './CheckoutPreview'
import {
  actions,
  builderGrid,
  copy,
  header,
  muted,
  nodeTitle,
  panel,
  previewColumn,
  railButton,
  railStack,
  stack,
  title,
} from './CheckoutBuilder.css'

export type CheckoutBuilderProps = {
  open: boolean
  productId?: string
  initialCheckoutId?: string
  onClose: () => void
}

type CheckoutDraftState = {
  checkoutId: string
  sourceKey: string
  draft: ProductCheckout
  saveState: FocusSaveState
}

function getCheckoutSourceKey(product: Product, checkout: ProductCheckout) {
  return `${product.updatedAt}:${checkout.id}`
}

function useCheckoutDraft(
  product: Product | undefined,
  checkout: ProductCheckout | undefined,
  open: boolean,
) {
  const [local, setLocal] = useState<CheckoutDraftState | null>(null)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (!open) setLocal(null)
  }

  if (!product || !checkout) {
    return {
      draft: null,
      saveState: 'saved' as FocusSaveState,
      patch: () => undefined,
      setSaveState: () => undefined,
    }
  }

  const sourceProduct = product
  const sourceCheckout = checkout
  const sourceKey = getCheckoutSourceKey(sourceProduct, sourceCheckout)
  const shouldUseLocal =
    local?.checkoutId === sourceCheckout.id &&
    (local.saveState !== 'saved' || local.sourceKey === sourceKey)
  const draft = shouldUseLocal ? local.draft : sourceCheckout
  const saveState = shouldUseLocal ? local.saveState : 'saved'

  function patch(input: Partial<ProductCheckout>) {
    setLocal((current) => {
      const currentIsUsable =
        current?.checkoutId === sourceCheckout.id &&
        (current.saveState !== 'saved' || current.sourceKey === sourceKey)
      const base = currentIsUsable ? current.draft : sourceCheckout
      return {
        checkoutId: sourceCheckout.id,
        sourceKey,
        draft: { ...base, ...input } satisfies ProductCheckout,
        saveState: 'dirty',
      }
    })
  }

  function setSaveState(nextSaveState: FocusSaveState) {
    setLocal((current) => ({
      checkoutId: sourceCheckout.id,
      sourceKey,
      draft: current?.checkoutId === sourceCheckout.id ? current.draft : sourceCheckout,
      saveState: nextSaveState,
    }))
  }

  return { draft, patch, saveState, setSaveState }
}

export function CheckoutBuilder({
  open,
  productId,
  initialCheckoutId,
  onClose,
}: CheckoutBuilderProps) {
  const productResult = useQuery<Product>({
    queryKey: productKeys.adminDetail(productId ?? ''),
    queryFn: async () => {
      const data = await apiFetch<unknown>(`/admin/products/${productId}`)
      return parseProductResponse(data)
    },
    enabled: Boolean(productId),
  })
  const product = productResult.data
  const selectedCheckoutId = initialCheckoutId
  const thankYouUrlRef = useRef<HTMLInputElement>(null)
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop')
  const [skin, setSkin] = useState<CheckoutSkin>('new')
  const selectedCheckout =
    product?.checkouts.find((checkout) => checkout.id === selectedCheckoutId) ??
    product?.checkouts[0]
  const { draft, patch, saveState, setSaveState } = useCheckoutDraft(
    product,
    selectedCheckout,
    open,
  )
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (!open) setSkin('new')
  }
  const updateCheckoutMutation = useUpdateCheckoutMutation(productId ?? '')

  async function saveCheckout() {
    if (!draft || !productId) return
    if (!isHttpsRedirectUrl(draft.thankYouUrl, selectedCheckout?.thankYouUrl)) {
      toast.error({ title: 'URL da página de obrigado inválida', description: thankYouUrlError })
      thankYouUrlRef.current?.focus()
      return
    }
    try {
      setSaveState('saving')
      await updateCheckoutMutation.mutateAsync({
        checkoutId: draft.id,
        input: { ...draft, thankYouUrl: draft.thankYouUrl.trim() },
      })
      setSaveState('saved')
      toast.success('Estilo salvo.')
    } catch (error) {
      setSaveState('dirty')
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível salvar o estilo',
          fallback: 'Suas alterações continuam nesta tela. Tente novamente.',
        }),
      )
    }
  }

  const styleSource = draft ?? selectedCheckout
  const skinOptions: Array<{ id: CheckoutSkin; title: string }> = [
    { id: 'new', title: 'Checkout 1' },
    { id: 'legacy', title: 'Checkout 2' },
  ]
  const rail = (
    <div className={railStack}>
      {styleSource ? (
        skinOptions.map((option) => {
          const optionCheckout = checkoutForSkin(styleSource, option.id)
          const inherited = option.id === 'legacy' && !styleSource.legacyPresentation
          return (
            <button
              key={option.id}
              type="button"
              className={railButton}
              data-active={skin === option.id}
              onClick={() => setSkin(option.id)}
            >
              <span className={nodeTitle}>{option.title}</span>
              <span className={muted}>
                {optionCheckout.template === 'classic' ? 'Clássico' : 'Minimalista'}
                {inherited ? ' · igual ao Checkout 1' : ''}
              </span>
            </button>
          )
        })
      ) : (
        <Card>Nenhum checkout encontrado.</Card>
      )}
    </div>
  )

  if (!productId || productResult.isPending || !product || !draft) {
    return (
      <FocusEditor
        open={open}
        onClose={onClose}
        breadcrumb="Produtos › Links"
        title="Editor de checkout"
        rail={rail}
      >
        <Card>Carregando...</Card>
      </FocusEditor>
    )
  }

  return (
    <FocusEditor
      open={open}
      onClose={onClose}
      breadcrumb={`Produtos › ${product.name}`}
      title="Editor de checkout"
      rail={rail}
      saveState={saveState}
      primaryLabel="Salvar estilo"
      primaryRightIcon={<Save size={16} />}
      onPrimaryAction={() => void saveCheckout()}
      primaryDisabled={updateCheckoutMutation.isPending}
    >
      <div className={stack}>
        <div className={header}>
          <div>
            <h2 className={title}>{skin === 'legacy' ? 'Checkout 2' : 'Checkout 1'}</h2>
            <p className={copy}>
              Aparência, campos e blocos desta variante. A outra variante guarda o estilo dela.
            </p>
          </div>
          <div className={actions}>
            <StatusBadge tone="neutral" label="Preview ao vivo" size="sm" />
          </div>
        </div>

        <div className={builderGrid}>
          <Card className={panel}>
            <CheckoutConfigControls
              thankYouUrlRef={thankYouUrlRef}
              originalThankYouUrl={selectedCheckout?.thankYouUrl}
              checkout={checkoutForSkin(draft, skin)}
              onChange={(input) => patch(applyCheckoutSkinPatch(draft, skin, input))}
            />
          </Card>
          <div className={previewColumn}>
            <CheckoutPreview
              product={product}
              checkout={draft}
              skin={skin}
              device={previewDevice}
              onDeviceChange={setPreviewDevice}
            />
          </div>
        </div>
      </div>
    </FocusEditor>
  )
}
